import { AgCharts } from 'ag-charts-react'
import { Tree } from 'primereact/tree'
import { classNames } from 'primereact/utils'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useRecoilValue } from 'recoil'
import { ConvergenceDashboardResponse, getConvergenceDashboard } from '../../api/convergence-analysis-API'
import { themeState } from '../../utils/state-atoms'

const oneLevelTreeStyle = {
  root: {
    className: '!bg-transparent !p-0 basis-0 grow overflow-y-auto no-toggler'
  },
  content: {
    className: '!p-0'
  },
  node: {
    className: '!p-0'
  },
  nodeCheckbox: {
    className: '!w-[18px] mr-1'
  },
  label: {
    className: 'text-xs'
  },
  input: {
    className: 'w-4 h-4'
  },
  container: {
    className: 'overflow-y-auto h-full'
  }
}

type TreeNode = {
  key: string
  label: string
  name?: string
  desc?: string
  children?: TreeNode[]
}

type AnalysisPanelProps = {
  title: string
  subtitle?: string
  className?: string
  status?: {
    label: string
    className: string
    dotClassName?: string
    tooltip?: string
  }
  onStatusClick?: () => void
  showStatusDot?: boolean
  children?: React.ReactNode
}

const emptyDashboardData: ConvergenceDashboardResponse = {
  projectRunNodes: [],
  stateNodes: [],
  selectedProjectRunKeys: {},
  selectedStateKeys: {},
  investigationSummary: {
    ruleNumber: '--',
    exportedAt: '--',
    runCount: 0,
    iterations: '--'
  },
  cdMetrics: [],
  clMetrics: [],
  fullTraceData: [],
  trendProjectionData: [],
  postConvergenceZoomData: [],
  queryErrors: []
}

const greenStatus = {
  label: 'green',
  className: 'bg-green-100 text-green-700',
  dotClassName: 'bg-green-500'
}

const redStatus = {
  label: 'red',
  className: 'bg-red-100 text-red-700',
  dotClassName: 'bg-red-500'
}

const unknownMetricStatus = {
  label: 'unknown',
  className: 'bg-gray-100 text-gray-600',
  dotClassName: 'bg-gray-400'
}

const formatChartValue = (value: number) => {
  if (!Number.isFinite(value)) {
    return ''
  }

  const absoluteValue = Math.abs(value)
  if (absoluteValue > 0 && (absoluteValue < 0.0001 || absoluteValue >= 10000)) {
    return value.toExponential(2)
  }

  return new Intl.NumberFormat('en-US', {
    maximumSignificantDigits: 4,
    maximumFractionDigits: 4
  }).format(value)
}

const formatChartNumber = ({ value }: { value: number }) => formatChartValue(value)

function roundChartValue(value: number): number
function roundChartValue(value: null): null
function roundChartValue(value: undefined): undefined
function roundChartValue(value: number | null | undefined) {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return value
  }

  return Number(value.toPrecision(6))
}

const chartTooltip = {
  renderer: ({ datum, xKey, yKey, yName }: any) => ({
    title: yName || yKey,
    content: `${datum?.[xKey] ?? ''}: ${formatChartValue(Number(datum?.[yKey]))}`
  })
}

const chartValueAxis = {
  type: 'number',
  position: 'left',
  label: {
    format: '.4~g',
    formatter: formatChartNumber
  },
  crosshair: {
    label: {
      format: '.4~g',
      formatter: formatChartNumber
    }
  }
}

const chartXAxis = {
  type: 'number',
  position: 'bottom'
}

const buildCheckedKeys = (nodes: TreeNode[]) => {
  return Object.fromEntries(
    nodes.flatMap(node => [
      [node.key, { checked: true }],
      ...(node.children || []).map(child => [child.key, { checked: true }])
    ])
  )
}

const getMetricStatusPill = (status?: 'red' | 'green' | 'unknown') => {
  if (status === 'red') {
    return redStatus
  }

  if (status === 'green') {
    return greenStatus
  }

  return unknownMetricStatus
}

const togglerTemplate = (node, options) => {
  if (!node) {
    return
  }

  const expanded = options.expanded
  const hasChildren = Boolean(node.children?.length)

  if (!hasChildren) {
    return <span className="p-tree-toggler p-link opacity-0 pointer-events-none" aria-hidden="true"></span>
  }

  const iconClassName = classNames('p-tree-toggler-icon pi pi-fw text-xs pr-1', {
    'pi-chevron-right': !expanded,
    'pi-chevron-down': expanded
  })

  return (
    <button
      type="button"
      className="p-tree-toggler p-link"
      tabIndex={0}
      aria-expanded={expanded}
      aria-label={`${node.name ?? node.label} ${expanded ? 'expanded' : 'collapsed'}`}
      onClick={options.onClick}>
      <span className={iconClassName} aria-hidden="true"></span>
    </button>
  )
}

const projectRunNodeTemplate = (node: TreeNode) => {
  if (!node) {
    return null
  }

  return (
    <span className="block truncate" title={node.label}>
      {node.name ?? node.label}
      {node.desc && <span className="text-neutral-500 dark:text-neutral-400"> ({node.desc})</span>}
    </span>
  )
}

type TreeSelectionMap = Record<string, { checked?: boolean; partialChecked?: boolean }>

export const normalizeSelectionMap = (
  value: unknown
): TreeSelectionMap => {
  if (!value || typeof value !== 'object') {
    return {}
  }

  if (Array.isArray(value)) {
    return {}
  }

  return Object.entries(value as Record<string, unknown>).reduce<TreeSelectionMap>((acc, [key, entry]) => {
    if (!entry || typeof entry !== 'object') {
      return acc
    }

    const selection = entry as { checked?: boolean; partialChecked?: boolean }
    acc[key] = {
      checked: Boolean(selection.checked),
      partialChecked: Boolean(selection.partialChecked)
    }
    return acc
  }, {})
}

const getNoDataSelectionError = (
  request: Record<string, any> = {},
  response: ConvergenceDashboardResponse
) => {
  const hasNoData =
    response.fullTraceData.length === 0 &&
    response.trendProjectionData.length === 0 &&
    response.postConvergenceZoomData.length === 0 &&
    response.cdMetrics.length === 0 &&
    response.clMetrics.length === 0

  if (!hasNoData) {
    return ''
  }

  const descriptors: string[] = []

  if (request.project || request.run) {
    const project = typeof request.project === 'string' ? request.project : ''
    const run = typeof request.run === 'string' ? request.run : ''
    descriptors.push(project && run ? `${project} / ${run}` : project || run)
  }

  if (Array.isArray(request.states)) {
    const requestedStates = request.states.filter((state): state is string => typeof state === 'string')
    const missingStates = requestedStates.filter(
      state => !response.stateNodes.some(node => node.key === state || node.label === state)
    )

    if (missingStates.length > 0) {
      descriptors.push(...missingStates)
    }
  }

  if (descriptors.length === 0) {
    return 'Selected project/run/state has no convergence analysis data.'
  }

  if (descriptors.length === 1) {
    return `${descriptors[0]} has no convergence analysis data.`
  }

  if (descriptors.length === 2) {
    return `${descriptors[0]} and ${descriptors[1]} have no convergence analysis data.`
  }

  return `${descriptors.slice(0, -1).join(', ')}, and ${descriptors[descriptors.length - 1]} have no convergence analysis data.`
}

export const getSelectionScope = (
  projectRunNodes: TreeNode[],
  stateNodes: TreeNode[],
  selectedProjectRunKeys: TreeSelectionMap = {},
  selectedStateKeys: TreeSelectionMap = {}
) => {
  const selectedProjectKeys = Object.entries(selectedProjectRunKeys)
    .filter(([, value]) => value?.checked || value?.partialChecked)
    .map(([key]) => key)

  const selectedStateNames = Object.entries(selectedStateKeys)
    .filter(([, value]) => value?.checked || value?.partialChecked)
    .map(([key]) => key)

  const selectedProjectRun = projectRunNodes
    .flatMap(projectNode => projectNode.children || [])
    .find(runNode => selectedProjectKeys.includes(runNode.key))

  const selectedProject = projectRunNodes.find(projectNode => selectedProjectKeys.includes(projectNode.key))

  const project = selectedProjectRun ? selectedProjectRun.key.split('::')[0] : selectedProject?.name || selectedProject?.label
  const run = selectedProjectRun ? selectedProjectRun.name || selectedProjectRun.label : undefined

  return {
    project,
    run,
    states: selectedStateNames.length > 0 ? selectedStateNames : [],
    hasSelection: Boolean(project && run && selectedStateNames.length > 0)
  }
}

const AnalysisPanel: FC<AnalysisPanelProps> = ({
  title,
  subtitle,
  className = '',
  status,
  onStatusClick,
  showStatusDot = false,
  children
}) => {
  return (
    <section
      className={`flex min-h-0 flex-col overflow-hidden rounded border border-gray-200 bg-white dark:border-neutral-700 dark:bg-neutral-80 ${className}`}>
      <div className="flex h-9 shrink-0 items-center justify-between gap-2 border-b border-gray-200 bg-gray-50 px-3 text-xs font-semibold text-gray-700 dark:border-neutral-700 dark:bg-neutral-90 dark:text-neutral-10">
        <div className="flex min-w-0 items-center gap-2">
          {showStatusDot && status?.dotClassName && (
            <span className={`h-2 w-2 shrink-0 rounded-full ${status.dotClassName}`} />
          )}
          <span className="truncate">{title}</span>
          {subtitle && (
            <span className="truncate text-[11px] font-normal text-gray-500 dark:text-neutral-400">
              {subtitle}
            </span>
          )}
        </div>
        {status && onStatusClick && (
          <button
            type="button"
            onClick={onStatusClick}
            title={status.tooltip}
            aria-label={status.tooltip ? `${status.label}: ${status.tooltip}` : status.label}
            className={`rounded-full px-2 py-0.5 text-[10px] font-semibold transition-opacity hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-gm-blue focus:ring-offset-1 dark:focus:ring-offset-neutral-90 ${status.className}`}>
            {status.label}
          </button>
        )}
        {status && !onStatusClick && (
          <span
            title={status.tooltip}
            className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${status.className}`}>
            {status.label}
          </span>
        )}
      </div>
      <div className="min-h-0 flex-1 p-3">{children}</div>
    </section>
  )
}

const ConvergenceAnalysis: FC = () => {
  const appTheme = useRecoilValue(themeState)
  const [dashboardData, setDashboardData] =
    useState<ConvergenceDashboardResponse>(emptyDashboardData)
  const [selectedProjectRunKeys, setSelectedProjectRunKeys] = useState<any>({})
  const [selectedStateKeys, setSelectedStateKeys] = useState<any>({})
  const [fullTraceWindow, setFullTraceWindow] = useState<{ start: number; end: number } | null>(null)
  const [fullTraceStartInput, setFullTraceStartInput] = useState('')
  const [fullTraceEndInput, setFullTraceEndInput] = useState('')
  const [trendProjectionWindow, setTrendProjectionWindow] = useState<{ start: number; end: number } | null>(null)
  const [trendProjectionStartInput, setTrendProjectionStartInput] = useState('')
  const [trendProjectionEndInput, setTrendProjectionEndInput] = useState('')
  const [postConvergenceWindow, setPostConvergenceWindow] = useState<{ start: number; end: number } | null>(null)
  const [postConvergenceStartInput, setPostConvergenceStartInput] = useState('')
  const [postConvergenceEndInput, setPostConvergenceEndInput] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const isMountedRef = useRef(true)
  const requestSequenceRef = useRef(0)
  const {
    projectRunNodes,
    stateNodes,
    investigationSummary,
    cdMetrics,
    clMetrics,
    cdStatus,
    clStatus,
    overallStatus,
    fullTraceData,
    trendProjectionData,
    postConvergenceZoomData
  } = dashboardData
  const allProjectRunKeys = useMemo(() => buildCheckedKeys(projectRunNodes), [projectRunNodes])
  const allStateKeys = useMemo(() => buildCheckedKeys(stateNodes), [stateNodes])
  const overallCardStatus = getMetricStatusPill(overallStatus)
  const cdCardStatus = getMetricStatusPill(cdStatus)
  const clCardStatus = getMetricStatusPill(clStatus)
  const fullTraceChartData = useMemo(
    () =>
      fullTraceData.map(point => ({
        ...point,
        linearMean: roundChartValue(point.linearMean),
        value: roundChartValue(point.value),
        trendBandUpper: roundChartValue(point.trendBandUpper),
        trendBandLower: roundChartValue(point.trendBandLower)
      })),
    [fullTraceData]
  )
  const trendProjectionChartData = useMemo(
    () =>
      trendProjectionData.map(point => ({
        ...point,
        value: roundChartValue(point.value),
        trendX10: roundChartValue(point.trendX10),
        toleranceUpper: roundChartValue(point.toleranceUpper),
        toleranceLower: roundChartValue(point.toleranceLower),
        proposed190: roundChartValue(point.proposed190)
      })),
    [trendProjectionData]
  )
  const postConvergenceZoomChartData = useMemo(
    () =>
      postConvergenceZoomData.map(point => ({
        ...point,
        value: roundChartValue(point.value),
        localSampled: roundChartValue(point.localSampled),
        residualP05: roundChartValue(point.residualP05),
        residualP95: roundChartValue(point.residualP95)
      })),
    [postConvergenceZoomData]
  )

  const loadDashboardData = useCallback(
    async (
      request: Record<string, any> = {},
      preserveSelection?: {
        projectRunKeys?: TreeSelectionMap
        stateKeys?: TreeSelectionMap
      }
    ) => {
      const requestSequence = ++requestSequenceRef.current
      setIsLoading(true)

      try {
        const response = await getConvergenceDashboard(request)

        if (!isMountedRef.current || requestSequence !== requestSequenceRef.current) {
          return
        }

        const explicitSelection = Boolean(
          request.project || request.run || (Array.isArray(request.states) && request.states.length > 0)
        )

        setDashboardData(response)
        setSelectedProjectRunKeys(
          explicitSelection && preserveSelection?.projectRunKeys
            ? preserveSelection.projectRunKeys
            : response.selectedProjectRunKeys || {}
        )
        setSelectedStateKeys(
          explicitSelection && preserveSelection?.stateKeys
            ? preserveSelection.stateKeys
            : response.selectedStateKeys || {}
        )

        const noDataError = getNoDataSelectionError(request, response)
        setLoadError(noDataError || (response.queryErrors || []).join(' | '))
      } catch {
        if (!isMountedRef.current || requestSequence !== requestSequenceRef.current) {
          return
        }

        setLoadError('Unable to load convergence analysis data.')
      } finally {
        if (!isMountedRef.current || requestSequence !== requestSequenceRef.current) {
          return
        }

        setIsLoading(false)
      }
    },
    []
  )

  const applySelection = useCallback(
    (nextProjectRunKeys: TreeSelectionMap = {}, nextStateKeys: TreeSelectionMap = {}) => {
      const scope = getSelectionScope(projectRunNodes, stateNodes, nextProjectRunKeys, nextStateKeys)

      setSelectedProjectRunKeys(nextProjectRunKeys)
      setSelectedStateKeys(nextStateKeys)

      if (!scope.hasSelection) {
        setLoadError('Select at least one project/run and state to view convergence analysis.')
        setIsLoading(false)
        return
      }

      void loadDashboardData(
        {
          project: scope.project,
          run: scope.run,
          states: scope.states
        },
        {
          projectRunKeys: nextProjectRunKeys,
          stateKeys: nextStateKeys
        }
      )
    },
    [loadDashboardData, projectRunNodes, stateNodes]
  )

  useEffect(() => {
    isMountedRef.current = true

    setLoadError('')
    void loadDashboardData()

    return () => {
      isMountedRef.current = false
    }
  }, [loadDashboardData])

  const fullTraceChartDataForView = useMemo(() => {
    if (!fullTraceWindow) {
      return fullTraceChartData
    }

    return fullTraceChartData.filter(
      point => point.iteration >= fullTraceWindow.start && point.iteration <= fullTraceWindow.end
    )
  }, [fullTraceChartData, fullTraceWindow])

  const fullTraceChartOptions = useMemo(
    () => ({
      data: fullTraceChartDataForView,
      theme: appTheme === 'dark' ? 'ag-default-dark' : 'ag-default',
      background: { fill: 'transparent' },
      series: [
        {
          type: 'line',
          xKey: 'iteration',
          yKey: 'linearMean',
          yName: 'Linear mean',
          tooltip: chartTooltip,
          stroke: '#0072ce',
          marker: { enabled: false },
          strokeWidth: 2
        },
        {
          type: 'line',
          xKey: 'iteration',
          yKey: 'value',
          yName: 'value',
          tooltip: chartTooltip,
          stroke: '#facc15',
          marker: { enabled: false },
          strokeWidth: 2
        },
        {
          type: 'line',
          xKey: 'iteration',
          yKey: 'trendBandUpper',
          yName: 'Trend band',
          tooltip: chartTooltip,
          stroke: '#16a34a',
          lineDash: [5, 4],
          marker: { enabled: false },
          strokeWidth: 2
        },
        {
          type: 'line',
          xKey: 'iteration',
          yKey: 'trendBandLower',
          yName: 'Trend band',
          tooltip: chartTooltip,
          stroke: '#16a34a',
          lineDash: [5, 4],
          marker: { enabled: false },
          strokeWidth: 2,
          showInLegend: false
        }
      ],
      axes: {
        x: {
          ...chartXAxis,
          min: fullTraceWindow ? fullTraceWindow.start : undefined,
          max: fullTraceWindow ? fullTraceWindow.end : undefined
        },
        y: chartValueAxis
      },
      legend: { position: 'top' }
    }),
    [appTheme, fullTraceChartDataForView, fullTraceWindow]
  )

  const trendProjectionChartDataForView = useMemo(() => {
    if (!trendProjectionWindow) {
      return trendProjectionChartData
    }

    return trendProjectionChartData.filter(
      point => point.iteration >= trendProjectionWindow.start && point.iteration <= trendProjectionWindow.end
    )
  }, [trendProjectionChartData, trendProjectionWindow])

  const trendProjectionChartOptions = useMemo(
    () => ({
      data: trendProjectionChartDataForView,
      theme: appTheme === 'dark' ? 'ag-default-dark' : 'ag-default',
      background: { fill: 'transparent' },
      series: [
        {
          type: 'line',
          xKey: 'iteration',
          yKey: 'toleranceUpper',
          yName: 'Allowed tolerance band',
          tooltip: chartTooltip,
          stroke: '#16a34a',
          lineDash: [5, 4],
          marker: { enabled: false },
          strokeWidth: 1.75
        },
        {
          type: 'line',
          xKey: 'iteration',
          yKey: 'toleranceLower',
          yName: 'Allowed tolerance band',
          tooltip: chartTooltip,
          stroke: '#16a34a',
          lineDash: [5, 4],
          marker: { enabled: false },
          strokeWidth: 1.75,
          showInLegend: false
        },
        {
          type: 'line',
          xKey: 'iteration',
          yKey: 'proposed190',
          yName: 'Projected trend',
          tooltip: chartTooltip,
          stroke: '#dc2626',
          marker: { enabled: false },
          strokeWidth: 3
        },
        {
          type: 'line',
          xKey: 'iteration',
          yKey: 'trendX10',
          yName: 'Trend x100',
          tooltip: chartTooltip,
          stroke: '#8b5cf6',
          lineDash: [7, 4],
          marker: { enabled: false },
          strokeWidth: 2
        },
        {
          type: 'line',
          xKey: 'iteration',
          yKey: 'value',
          yName: 'Current value',
          tooltip: chartTooltip,
          stroke: '#facc15',
          lineDash: [2, 4],
          marker: { enabled: false },
          strokeWidth: 2
        }
      ],
      axes: {
        x: {
          ...chartXAxis,
          min: trendProjectionWindow ? trendProjectionWindow.start : undefined,
          max: trendProjectionWindow ? trendProjectionWindow.end : undefined
        },
        y: chartValueAxis
      },
      legend: { position: 'top' }
    }),
    [appTheme, trendProjectionChartDataForView, trendProjectionWindow]
  )

  const postConvergenceChartDataForView = useMemo(() => {
    if (!postConvergenceWindow) {
      return postConvergenceZoomChartData
    }

    return postConvergenceZoomChartData.filter(
      point => point.sample >= postConvergenceWindow.start && point.sample <= postConvergenceWindow.end
    )
  }, [postConvergenceWindow, postConvergenceZoomChartData])

  const postConvergenceZoomChartOptions = useMemo(
    () => ({
      data: postConvergenceChartDataForView,
      theme: appTheme === 'dark' ? 'ag-default-dark' : 'ag-default',
      background: { fill: 'transparent' },
      series: [
        {
          type: 'line',
          xKey: 'sample',
          yKey: 'value',
          yName: 'residual p95',
          tooltip: chartTooltip,
          stroke: '#facc15',
          marker: { enabled: false },
          strokeWidth: 2
        },
        {
          type: 'line',
          xKey: 'sample',
          yKey: 'localSampled',
          yName: 'residual p05',
          tooltip: chartTooltip,
          stroke: '#0072ce',
          lineDash: [5, 4],
          marker: { enabled: false },
          strokeWidth: 2
        }
      ],
      axes: {
        x: {
          ...chartXAxis,
          min: postConvergenceWindow ? postConvergenceWindow.start : undefined,
          max: postConvergenceWindow ? postConvergenceWindow.end : undefined
        },
        y: chartValueAxis
      },
      legend: { position: 'top' }
    }),
    [appTheme, postConvergenceChartDataForView, postConvergenceWindow]
  )

  return (
    <div className="flex h-[calc(100vh-160px)] min-h-[520px] items-stretch p-4">
      <div className="flex w-[320px] shrink-0 flex-col items-stretch space-y-4 overflow-hidden">
        <div className="basis-0 grow flex flex-col items-stretch space-y-2 p-4 !border border-solid rounded-lg dark:border-neutral-700">
          <div className="flex items-center justify-between">
            <div className="font-semibold text-left text-gm-blue-dark dark:text-neutral-20">
              Project - Run
            </div>
            <div className="flex items-center space-x-4">
              <button
                type="button"
                onClick={() => applySelection(allProjectRunKeys, selectedStateKeys)}
                className="text-xs text-gm-blue hover:underline">
                Select All
              </button>
              <button
                type="button"
                onClick={() => applySelection({}, selectedStateKeys)}
                className="text-xs text-gm-blue hover:underline">
                Deselect All
              </button>
            </div>
          </div>
          <Tree
            value={projectRunNodes}
            selectionKeys={selectedProjectRunKeys}
            onSelectionChange={event => applySelection(normalizeSelectionMap(event.value), selectedStateKeys)}
            selectionMode="checkbox"
            className="h-full border-0 checkbox-tree"
            togglerTemplate={togglerTemplate}
            nodeTemplate={projectRunNodeTemplate}
            style={{ minHeight: 'calc(100% - 30px)', maxHeight: 'calc(100% - 30px)' }}
            pt={{
              root: {
                className: '!p-0 basis-0 grow !bg-transparent'
              },
              content: {
                className: '!p-0 min-w-0'
              },
              node: {
                className: '!p-0'
              },
              nodeCheckbox: {
                className: '!w-[18px] mr-1'
              },
              label: {
                className: 'text-xs min-w-0 flex-1 overflow-hidden'
              },
              input: {
                className: 'w-4 h-4'
              },
              container: {
                className: 'overflow-y-auto overflow-x-hidden h-full'
              }
            }}
          />
        </div>
        <div className="basis-0 grow flex flex-col items-stretch space-y-2 p-4 !border border-solid rounded-lg dark:border-neutral-700">
          <div className="flex items-center justify-between">
            <div className="font-semibold text-left text-gm-blue-dark dark:text-neutral-20">
              States
            </div>
            <div className="flex items-center space-x-4">
              <button
                type="button"
                onClick={() => applySelection(selectedProjectRunKeys, allStateKeys)}
                className="text-xs text-gm-blue hover:underline">
                Select All
              </button>
              <button
                type="button"
                onClick={() => applySelection(selectedProjectRunKeys, {})}
                className="text-xs text-gm-blue hover:underline">
                Deselect All
              </button>
            </div>
          </div>
          <Tree
            value={stateNodes}
            selectionKeys={selectedStateKeys}
            onSelectionChange={event => applySelection(selectedProjectRunKeys, normalizeSelectionMap(event.value))}
            selectionMode="checkbox"
            className="h-full border-0 checkbox-tree"
            togglerTemplate={togglerTemplate}
            style={{ minHeight: 'calc(100% - 30px)', maxHeight: 'calc(100% - 30px)' }}
            pt={oneLevelTreeStyle}
          />
        </div>
      </div>
      <div className="relative flex min-w-0 flex-1 flex-col px-6">
        {isLoading && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/75 backdrop-blur-[1px] dark:bg-neutral-900/60">
            <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white/90 px-4 py-3 shadow-sm dark:border-neutral-700 dark:bg-neutral-90/90">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-gm-blue border-t-transparent" />
              <span className="text-sm font-medium text-gray-700 dark:text-neutral-200">
                Loading convergence data...
              </span>
            </div>
          </div>
        )}
        <div className="flex flex-col items-start">
          <h1 className="text-2xl font-semibold text-gray-800 dark:text-neutral-10">
            CFD Traffic-light Investigation
          </h1>
          <div className="mt-1 text-sm text-gray-500 dark:text-neutral-400">
            Methodology Version {investigationSummary.ruleNumber} - exported {investigationSummary.exportedAt} -{' '}
            {investigationSummary.runCount} runs
          </div>
          {isLoading && (
            <div className="mt-2 text-xs text-gray-500 dark:text-neutral-400">
              Loading convergence data...
            </div>
          )}
          {loadError && (
            <div className="mt-2 max-w-full text-xs text-red-600 break-words">{loadError}</div>
          )}
        </div>
        <div className="mt-4 flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pr-1">
          <div className="grid min-h-[150px] grid-cols-[150px_minmax(0,1fr)_minmax(0,1fr)] gap-4">
            <AnalysisPanel title="Overall" status={overallCardStatus}>
              <div className="flex h-full flex-col justify-center">
                <div className="text-gm-blue">
                  <span className="text-4xl font-semibold leading-none">
                    {investigationSummary.iterations}
                  </span>
                  <span className="ml-1 text-base font-semibold">iters</span>
                </div>
              </div>
            </AnalysisPanel>
            <AnalysisPanel title="CD" status={cdCardStatus} showStatusDot>
              <div className="flex h-full flex-col justify-center gap-2">
                {cdMetrics.map(metric => (
                  <div key={metric.label} className="flex items-start justify-between gap-4 text-xs">
                    <span className="text-gray-500 dark:text-neutral-400">{metric.label}</span>
                    <span className="text-right font-medium text-gray-900 dark:text-neutral-10">
                      {metric.value}
                    </span>
                  </div>
                ))}
              </div>
            </AnalysisPanel>
            <AnalysisPanel title="CL" status={clCardStatus} showStatusDot>
              <div className="flex h-full flex-col justify-center gap-2">
                {clMetrics.map(metric => (
                  <div key={metric.label} className="flex items-start justify-between gap-4 text-xs">
                    <span className="text-gray-500 dark:text-neutral-400">{metric.label}</span>
                    <span className="text-right font-medium text-gray-900 dark:text-neutral-10">
                      {metric.value}
                    </span>
                  </div>
                ))}
              </div>
            </AnalysisPanel>
          </div>
          <AnalysisPanel
            title="Full trace"
            subtitle="(fixed tolerance band from unchanging window)"
            className="min-h-[220px]">
            <div className="flex h-full min-h-0 flex-col">
              <div className="mb-2 flex items-center gap-2 text-xs">
                <label className="flex items-center gap-1">
                  <span className="text-gray-600 dark:text-neutral-300">Start</span>
                  <input
                    type="number"
                    value={fullTraceStartInput}
                    onChange={event => setFullTraceStartInput(event.target.value)}
                    className="w-20 rounded border border-gray-300 bg-white px-1 py-0.5 text-xs dark:border-neutral-600 dark:bg-neutral-90 dark:text-neutral-10"
                    placeholder="0"
                  />
                </label>
                <label className="flex items-center gap-1">
                  <span className="text-gray-600 dark:text-neutral-300">End</span>
                  <input
                    type="number"
                    value={fullTraceEndInput}
                    onChange={event => setFullTraceEndInput(event.target.value)}
                    className="w-20 rounded border border-gray-300 bg-white px-1 py-0.5 text-xs dark:border-neutral-600 dark:bg-neutral-90 dark:text-neutral-10"
                    placeholder="100"
                  />
                </label>
                <button
                  type="button"
                  onClick={() => {
                    const start = Number(fullTraceStartInput)
                    const end = Number(fullTraceEndInput)
                    if (!Number.isFinite(start) || !Number.isFinite(end) || start >= end) {
                      return
                    }

                    setFullTraceWindow({ start, end })
                  }}
                  className="rounded bg-gm-blue px-2 py-1 font-medium text-white hover:opacity-90">
                  Focus window
                </button>
                {fullTraceWindow && (
                  <button
                    type="button"
                    onClick={() => {
                      setFullTraceWindow(null)
                      setFullTraceStartInput('')
                      setFullTraceEndInput('')
                    }}
                    className="text-gm-blue hover:underline">
                    Reset
                  </button>
                )}
              </div>
              <div className="min-h-0 flex-1">
                <AgCharts
                  options={fullTraceChartOptions as any}
                  style={{ height: '100%', width: '100%' }}
                />
              </div>
              <div className="shrink-0 text-center text-xs font-medium text-gray-600 dark:text-neutral-300">
                Iteration
              </div>
            </div>
          </AnalysisPanel>
          <AnalysisPanel
            title="Trend projection"
            subtitle="x100 iterations"
            className="min-h-[220px]">
            <div className="flex h-full min-h-0 flex-col">
              <div className="mb-2 flex items-center gap-2 text-xs">
                <label className="flex items-center gap-1">
                  <span className="text-gray-600 dark:text-neutral-300">Start</span>
                  <input
                    type="number"
                    value={trendProjectionStartInput}
                    onChange={event => setTrendProjectionStartInput(event.target.value)}
                    className="w-20 rounded border border-gray-300 bg-white px-1 py-0.5 text-xs dark:border-neutral-600 dark:bg-neutral-90 dark:text-neutral-10"
                    placeholder="0"
                  />
                </label>
                <label className="flex items-center gap-1">
                  <span className="text-gray-600 dark:text-neutral-300">End</span>
                  <input
                    type="number"
                    value={trendProjectionEndInput}
                    onChange={event => setTrendProjectionEndInput(event.target.value)}
                    className="w-20 rounded border border-gray-300 bg-white px-1 py-0.5 text-xs dark:border-neutral-600 dark:bg-neutral-90 dark:text-neutral-10"
                    placeholder="100"
                  />
                </label>
                <button
                  type="button"
                  onClick={() => {
                    const start = Number(trendProjectionStartInput)
                    const end = Number(trendProjectionEndInput)
                    if (!Number.isFinite(start) || !Number.isFinite(end) || start >= end) {
                      return
                    }

                    setTrendProjectionWindow({ start, end })
                  }}
                  className="rounded bg-gm-blue px-2 py-1 font-medium text-white hover:opacity-90">
                  Focus window
                </button>
                {trendProjectionWindow && (
                  <button
                    type="button"
                    onClick={() => {
                      setTrendProjectionWindow(null)
                      setTrendProjectionStartInput('')
                      setTrendProjectionEndInput('')
                    }}
                    className="text-gm-blue hover:underline">
                    Reset
                  </button>
                )}
              </div>
              <div className="min-h-0 flex-1">
                <AgCharts
                  options={trendProjectionChartOptions as any}
                  style={{ height: '100%', width: '100%' }}
                />
              </div>
              <div className="shrink-0 text-center text-xs font-medium text-gray-600 dark:text-neutral-300">
                Iteration
              </div>
            </div>
          </AnalysisPanel>
          <AnalysisPanel
            title="Post-convergence zoom"
            subtitle="(local 0-90th percentile, 50-sample window)"
            className="min-h-[220px]">
            <div className="flex h-full min-h-0 flex-col">
              <div className="mb-2 flex items-center gap-2 text-xs">
                <label className="flex items-center gap-1">
                  <span className="text-gray-600 dark:text-neutral-300">Start</span>
                  <input
                    type="number"
                    value={postConvergenceStartInput}
                    onChange={event => setPostConvergenceStartInput(event.target.value)}
                    className="w-20 rounded border border-gray-300 bg-white px-1 py-0.5 text-xs dark:border-neutral-600 dark:bg-neutral-90 dark:text-neutral-10"
                    placeholder="0"
                  />
                </label>
                <label className="flex items-center gap-1">
                  <span className="text-gray-600 dark:text-neutral-300">End</span>
                  <input
                    type="number"
                    value={postConvergenceEndInput}
                    onChange={event => setPostConvergenceEndInput(event.target.value)}
                    className="w-20 rounded border border-gray-300 bg-white px-1 py-0.5 text-xs dark:border-neutral-600 dark:bg-neutral-90 dark:text-neutral-10"
                    placeholder="100"
                  />
                </label>
                <button
                  type="button"
                  onClick={() => {
                    const start = Number(postConvergenceStartInput)
                    const end = Number(postConvergenceEndInput)
                    if (!Number.isFinite(start) || !Number.isFinite(end) || start >= end) {
                      return
                    }

                    setPostConvergenceWindow({ start, end })
                  }}
                  className="rounded bg-gm-blue px-2 py-1 font-medium text-white hover:opacity-90">
                  Focus window
                </button>
                {postConvergenceWindow && (
                  <button
                    type="button"
                    onClick={() => {
                      setPostConvergenceWindow(null)
                      setPostConvergenceStartInput('')
                      setPostConvergenceEndInput('')
                    }}
                    className="text-gm-blue hover:underline">
                    Reset
                  </button>
                )}
              </div>
              <div className="min-h-0 flex-1">
                <AgCharts
                  options={postConvergenceZoomChartOptions as any}
                  style={{ height: '100%', width: '100%' }}
                />
              </div>
              <div className="shrink-0 text-center text-xs font-medium text-gray-600 dark:text-neutral-300">
                Sample
              </div>
            </div>
          </AnalysisPanel>
        </div>
      </div>
    </div>
  )
}

export default ConvergenceAnalysis